/**
 * Kollegen-Suche für „Weiterleiten" im Anruf-Popup — reine Funktionen, ohne
 * React und ohne Netz (Vitest-tauglich).
 *
 * Datenbasis: die Nebenstellen-Zuordnungen der Telefonanlage
 * (`GET /telephony/mappings`: Durchwahl ↔ KMVS-Benutzer) plus die Benutzerliste
 * (`GET /users`), aus der Name und KMVS-Kürzel (`username`) stammen.
 *
 * Suche:
 *  - Name als Teilstring (Vor-/Nachname, beliebige Reihenfolge; mehrere Wörter
 *    müssen ALLE passen),
 *  - Kürzel als Präfix,
 *  - Durchwahl als Präfix,
 *  jeweils ohne Groß-/Kleinschreibung; Umlaute und „ae/oe/ue/ss" gelten als gleich
 *  („Müller" findet man mit „mueller" und umgekehrt).
 */

export interface KollegeMapping {
    extension: string;
    userId: string;
    displayName?: string | null;
    email?: string | null;
}

export interface KollegeUser {
    id: string;
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
    email?: string | null;
}

export interface Kollege {
    userId: string;
    /** Durchwahl (Nebenstelle) — das eigentliche Weiterleitungs-Ziel. */
    extension: string;
    /** Anzeigename, z. B. „Anna Müller". */
    name: string;
    /** KMVS-Kürzel (`user.username`), falls gepflegt. */
    kuerzel: string | null;
}

/**
 * Für den Vergleich normalisieren: klein, Umlaute ausgeschrieben, übrige
 * diakritische Zeichen entfernt, Mehrfach-Leerraum zusammengezogen.
 */
export function normalisiere(s: string): string {
    return s
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Sieht die Eingabe wie eine Telefonnummer / Durchwahl aus (nur Ziffern und
 * übliche Trennzeichen, mindestens eine Ziffer)? Dann wird sie direkt als Ziel
 * genommen, ohne Auswahl aus der Liste.
 */
export function istNummer(eingabe: string): boolean {
    const t = eingabe.trim();
    return /^[+\d][\d\s\-/()]*$/.test(t) && /\d/.test(t);
}

const personName = (u: KollegeUser | undefined, m: KollegeMapping): string => {
    const first = u?.firstName?.trim() ?? "";
    const last = u?.lastName?.trim() ?? "";
    if (first || last) return [first, last].filter(Boolean).join(" ");
    return (
        u?.name?.trim() ||
        m.displayName?.trim() ||
        u?.username?.trim() ||
        m.email?.trim() ||
        u?.email?.trim() ||
        `Durchwahl ${m.extension}`
    );
};

/**
 * Nebenstellen-Zuordnungen mit den Benutzern zu einer Kollegen-Liste
 * zusammenführen. Die eigene Durchwahl fällt heraus (an sich selbst
 * weiterleiten ergibt keinen Sinn), Einträge ohne Durchwahl ebenso.
 * Sortiert nach Name.
 */
export function baueKollegen(
    mappings: KollegeMapping[],
    users: KollegeUser[],
    eigeneDurchwahl?: string | null,
): Kollege[] {
    const byId = new Map(users.map((u) => [u.id, u] as const));
    const eigene = eigeneDurchwahl?.trim() || null;
    const out: Kollege[] = [];
    const gesehen = new Set<string>();
    for (const m of mappings) {
        const ext = (m.extension ?? "").trim();
        if (!ext || ext === eigene || gesehen.has(ext)) continue;
        gesehen.add(ext);
        const u = byId.get(m.userId);
        out.push({
            userId: m.userId,
            extension: ext,
            name: personName(u, m),
            kuerzel: u?.username?.trim() || null,
        });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/**
 * Trefferqualität eines Kollegen für die (normalisierte) Eingabe; `null` = passt
 * nicht. Kleinere Zahl = besser.
 *  0 Kürzel exakt · 1 Kürzel-Präfix · 2 Durchwahl exakt · 3 Wortanfang im Namen
 *  · 4 Durchwahl-Präfix · 5 Teilstring im Namen.
 */
function rang(k: Kollege, q: string): number | null {
    const kuerzel = k.kuerzel ? normalisiere(k.kuerzel) : "";
    if (kuerzel && kuerzel === q) return 0;
    if (kuerzel && kuerzel.startsWith(q)) return 1;
    if (k.extension === q) return 2;

    const name = normalisiere(k.name);
    const woerter = q.split(" ");
    const alleImNamen = woerter.every((w) => name.includes(w));
    if (alleImNamen) {
        const namensWoerter = name.split(" ");
        const wortAnfang = woerter.every((w) => namensWoerter.some((nw) => nw.startsWith(w)));
        if (wortAnfang) return 3;
    }
    if (k.extension.startsWith(q)) return 4;
    if (alleImNamen) return 5;
    return null;
}

/**
 * Passende Kollegen zur Eingabe, beste Treffer zuerst (bei Gleichstand nach
 * Name). Leere Eingabe → keine Vorschläge.
 */
export function sucheKollegen(kollegen: Kollege[], eingabe: string, max = 6): Kollege[] {
    const q = normalisiere(eingabe);
    if (!q) return [];
    const treffer: Array<{ k: Kollege; r: number }> = [];
    for (const k of kollegen) {
        const r = rang(k, q);
        if (r !== null) treffer.push({ k, r });
    }
    treffer.sort((a, b) => a.r - b.r || a.k.name.localeCompare(b.k.name, "de"));
    return treffer.slice(0, max).map((t) => t.k);
}
